import { Injectable, TemplateRef, inject } from '@angular/core';
import { Validators } from '@angular/forms';

import { Observable, of } from 'rxjs';

import { CamAddEventRequest } from '@camelot/calendar';
import {
  CamAbstractInput,
  InputChoices,
  InputLabel,
  InputPanel,
  InputTextBox,
  InputTimePicker,
  computeTimeRanges,
  extractTimeFromDate,
  formDateTimeSection,
} from '@camelot/form-model';
import { CamUserInputService, EUserFormFields } from '@camelot/user';
import { extractEnum } from '@camelot/utils';

import { UnavailabilityCreationPayloadInput } from '../dto/post/unavailability-creation-payload-input';
import { UnavailabilityModifierPayloadInput } from '../dto/post/unavailability-modifier-payload-input';
import { CalendarUnavailability } from '../dto/unavailability';
import { UnavailabilityType } from '../dto/unavailability-type';

export enum EUnavailabilityFormFields {
  start = 'start',
  end = 'end',
  type = 'type',
  title = 'title',
  isAllDay = 'isAllDay',
}

type OptionFields = {
  noLabel?: boolean;
  options$?: Observable<unknown>;
  visible$?: Observable<boolean>;
};

type Template = 'type';

@Injectable({
  providedIn: 'root',
})
export class CamUnavailabilityFormService {
  private _usersInputService = inject(CamUserInputService);

  public getEventForm(
    template: { [key in Partial<Template>]: TemplateRef<any> },
    data?: CamAddEventRequest
  ): CamAbstractInput[] {
    const panelClass = 'highlight-title space-between color-border-primary py-space-md';

    const titleInput = new InputTextBox({
      key: EUnavailabilityFormFields.title,
      label: 'unavailabilities.form.title',
      validators: [Validators.required],
      value: data?.title ?? '',
    });
    const userInput = this._usersInputService.getUserInput(data?.userId ?? '', {
      label: 'unavailabilities.form.assignee',
    });
    const typeInput = new InputChoices({
      key: EUnavailabilityFormFields.type,
      label: 'unavailabilities.form.type',
      multiple: false,
      options: of(
        extractEnum(UnavailabilityType, true).map(item => ({
          id: item.value.toString(),
          name: `planning.unavailability-type.${item.name.toLowerCase()}`,
          data: item.value,
        }))
      ),
      value: data?.type ? [data.type.toString()] : [],
      choiceTemplate: template ? { one: template['type'] } : {},
    });

    return [
      new InputPanel({
        key: 'panel-spacer',
        label: '',
        containerClass: [panelClass],
        contentClass: 'row mb-space-md',
        children: [
          new InputPanel({
            key: 'panel-main',
            label: 'unavailabilities.form.title',
            containerClass: [],
            contentClass: '',
            children: [
              new InputPanel({
                key: 'panel-main-grid',
                label: '',
                containerClass: [],
                contentClass: 'row g-space-md',
                children: [titleInput, userInput, typeInput],
              }),
            ],
          }),
          formDateTimeSection({ start: data?.startDate?.toString(), end: data?.endDate?.toString() }),
        ],
      }),
    ];
  }

  public formatEventForm(data: any): UnavailabilityCreationPayloadInput[] {
    const title = data[EUnavailabilityFormFields.title] ?? '';
    const userId = data[EUserFormFields.user] ? data[EUserFormFields.user][0] : '';
    const type = data[EUnavailabilityFormFields.type]
      ? Number(data[EUnavailabilityFormFields.type][0])
      : UnavailabilityType.Unknown;
    const isAllDay = data[EUnavailabilityFormFields.isAllDay] ?? false;

    const ranges = computeTimeRanges(data);
    return ranges.map(({ start, end }) => ({
      start,
      end,
      isAllDay,
      title,
      userId,
      type,
    }));
  }

  public formatUpdateForm(id: string, data: any): Partial<UnavailabilityModifierPayloadInput> {
    return {
      id: id,
      start: data[EUnavailabilityFormFields.start] ?? null,
      end: data[EUnavailabilityFormFields.end] ?? null,
      isAllDay: data[EUnavailabilityFormFields.isAllDay] ?? false,
      userId: data[EUserFormFields.user] ? data[EUserFormFields.user][0] : null,
      type: data[EUnavailabilityFormFields.type]
        ? Number(data[EUnavailabilityFormFields.type][0])
        : UnavailabilityType.Unknown,
    };
  }

  public getFields(
    fields: EUnavailabilityFormFields,
    unavailability: CalendarUnavailability | null,
    template: { [key in Template]: TemplateRef<any> } | null,
    options?: OptionFields
  ): CamAbstractInput {
    switch (fields) {
      case EUnavailabilityFormFields.start:
        return new InputTimePicker({
          key: EUnavailabilityFormFields.start,
          label: options?.noLabel === true ? '' : 'unavailabilitys.form.unavailability.startHour',
          validators: [Validators.required],
          value: extractTimeFromDate(unavailability?.start ?? ''),
        });
      case EUnavailabilityFormFields.end:
        return new InputTimePicker({
          key: EUnavailabilityFormFields.end,
          label: options?.noLabel === true ? '' : 'unavailabilitys.form.unavailability.end',
          value: extractTimeFromDate(unavailability?.end ?? ''),
          min: extractTimeFromDate(unavailability?.start ?? ''),
          validators: [Validators.required],
        });
      case EUnavailabilityFormFields.type:
        return new InputChoices({
          key: EUnavailabilityFormFields.type,
          label: options?.noLabel === true ? '' : 'unavailability.form.type',
          multiple: false,
          options: of(
            extractEnum(UnavailabilityType, true).map(item => ({
              id: item.value.toString(),
              name: `planning.unavailability-type.${item.name.toLowerCase()}`,
              data: item.value,
            }))
          ),
          value: unavailability?.type ? [unavailability.type.toString()] : [],
          choiceTemplate: template ? { one: template['type'] } : {},
        });
      default:
        return new InputLabel({
          key: 'nokey',
          label: '',
        });
    }
  }
}
